//? React
import { useMemo, useState } from "react";

//? React Query
import { useQuery } from "@tanstack/react-query";

//? External
import { Button, Spinner, Form, Row, Col, Card, Badge } from "react-bootstrap";
import { FaPlus, FaCheck, FaEraser, FaEye } from "react-icons/fa";
import { RiArrowGoBackLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

//* Data
import { questionAPI } from "../../../hooks/questions/questionsAPI";
import { disciplineAPI } from "../../../hooks/disciplines/disciplinesAPI";
import { useQuestionCart } from "../../../hooks/questionCart/useQuestionCart";
import { useDebounce } from "../../../hooks/useDebounce";

//* Cart button + question viewer modal
import QuestionCartButton from "../../../component/QuestionCart/QuestionCartButton";
import ModalManage from "../list/components/ModalManage";

const PER_PAGE = 12;
const PHASES = ["1a fase", "2a fase"];

/**
 * Lists public exam-board ("banca") questions and lets ADMIN/TEACHER add them
 * to the persistent question cart.
 */
export default function BancaQuestionList() {
  const token = useSelector((state: any) => state.auth.token);
  const navigate = useNavigate();

  const hasExamDraft = !!localStorage.getItem("exam_form_draft");

  // searchInput = valor digitado; search = valor aplicado (com debounce)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tag, setTag] = useState("");
  const [disciplineId, setDisciplineId] = useState("");
  const [year, setYear] = useState("");
  const [phase, setPhase] = useState("");
  const [page, setPage] = useState(1);

  const { isInCart, addToCart, isAdding } = useQuestionCart(token);
  const [addingId, setAddingId] = useState<string | null>(null);

  // Questão sendo visualizada no modal
  const [viewQuestionId, setViewQuestionId] = useState<string | null>(null);

  // Aplica a busca após o usuário parar de digitar
  const applySearch = useDebounce((value: string) => {
    setSearch(value);
    setPage(1);
  }, 500);

  // Aplica o filtro de tag após o usuário parar de digitar
  const applyTag = useDebounce((value: string) => {
    setTag(value);
    setPage(1);
  }, 500);

  // Anos disponíveis (2013 até o ano atual), em ordem decrescente
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const list: number[] = [];
    for (let y = current; y >= 2013; y--) {
      list.push(y);
    }
    return list;
  }, []);

  // Carrega todas as disciplinas para o dropdown
  const { data: disciplines = [] } = useQuery({
    queryKey: ["disciplines", "all-for-banca"],
    queryFn: async () => {
      const res = await disciplineAPI.getAll("", 1, token, 100);
      return res?.disciplines ?? [];
    },
    enabled: !!token,
    staleTime: 10 * 60 * 1000,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["questions", "banca", { search, disciplineId, year, phase, tag, page }],
    queryFn: () =>
      questionAPI.getAll(
        search,
        page,
        token,
        PER_PAGE,
        disciplineId || undefined,
        year ? Number(year) : undefined,
        phase || undefined,
        true,
        undefined,
        tag || undefined
      ),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const questions = data?.questions ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const totalItems = pagination?.totalItems ?? questions.length;

  const hasActiveFilters =
    !!search || !!disciplineId || !!year || !!phase || !!tag;

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setTagInput("");
    setTag("");
    setDisciplineId("");
    setYear("");
    setPhase("");
    setPage(1);
  };

  const handleAdd = async (questionId: string) => {
    setAddingId(questionId);
    try {
      await addToCart({ questionId });
    } catch (error) {
      console.error("Erro ao adicionar questão à lista:", error);
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="d-flex flex-column gap-3 p-3">
      <div className="d-flex align-items-center justify-content-between">
        <h3 className="mb-0">Questões de Banca</h3>
        {hasExamDraft && (
          <Button
            id="btn-back-to-exam"
            type="button"
            variant="success"
            className="d-flex align-items-center"
            onClick={() => navigate("/exams/create")}
          >
            <RiArrowGoBackLine style={{ marginRight: 8 }} />
            Voltar para a prova
          </Button>
        )}
      </div>

      <Form>
        <Row className="g-2 align-items-end">
          <Col md={3}>
            <Form.Label className="mb-1 small text-muted">Pesquisar</Form.Label>
            <Form.Control
              id="banca-search"
              type="text"
              placeholder="Pesquisar enunciado"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                applySearch(e.target.value);
              }}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="mb-1 small text-muted">Tag</Form.Label>
            <Form.Control
              id="banca-tag"
              type="text"
              placeholder="Filtrar por tag (ex: função)"
              value={tagInput}
              onChange={(e) => {
                setTagInput(e.target.value);
                applyTag(e.target.value);
              }}
            />
          </Col>
          <Col md={3}>
            <Form.Label className="mb-1 small text-muted">Disciplina</Form.Label>
            <Form.Select
              id="banca-discipline"
              value={disciplineId}
              onChange={(e) => {
                setDisciplineId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              {disciplines.map((discipline: { id: string; name: string }) => (
                <option key={discipline.id} value={discipline.id}>
                  {discipline.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="mb-1 small text-muted">Ano</Form.Label>
            <Form.Select
              id="banca-year"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="mb-1 small text-muted">Fase</Form.Label>
            <Form.Select
              id="banca-phase"
              value={phase}
              onChange={(e) => {
                setPhase(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              {PHASES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={1}>
            <Button
              id="btn-clear-banca-filters"
              type="button"
              variant="outline-secondary"
              className="w-100 d-flex align-items-center justify-content-center"
              aria-label="Limpar filtros"
              disabled={!hasActiveFilters}
              onClick={handleClearFilters}
            >
              <FaEraser />
            </Button>
          </Col>
        </Row>
      </Form>

      {!isLoading && !isError && (
        <small className="text-muted">
          {totalItems} questão(ões) encontrada(s)
          {hasActiveFilters ? " com os filtros aplicados" : ""}.
        </small>
      )}

      {isLoading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" role="status" />
        </div>
      ) : isError ? (
        <p className="text-danger">Erro ao carregar as questões de banca.</p>
      ) : questions.length === 0 ? (
        <p className="text-muted text-center py-4">Nenhuma questão encontrada.</p>
      ) : (
        <>
          <div className="d-flex flex-column gap-2">
            {questions.map((question) => {
              const inCart = isInCart(question.id);
              return (
                <Card key={question.id}>
                  <Card.Body className="d-flex justify-content-between align-items-center gap-3">
                    <div className="d-flex flex-column gap-1">
                      <strong>{question.displayLabel ?? "Questão"}</strong>
                      <small className="text-muted">
                        {question.discipline?.name ?? "-"}
                        {question.year ? ` · ${question.year}` : ""}
                        {question.phase ? ` · ${question.phase}` : ""}
                      </small>
                      {question.tags && question.tags.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {question.tags.map((tag) => (
                            <Badge
                              key={tag.id}
                              pill
                              style={{
                                backgroundColor: tag.color || "#6c757d",
                                color: "#fff",
                                fontWeight: 500,
                              }}
                            >
                              {tag.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <Button
                        id={`btn-view-${question.id}`}
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        aria-label="Ver questão completa"
                        onClick={() => setViewQuestionId(question.id)}
                      >
                        <FaEye className="me-1" /> Ver questão
                      </Button>

                      {inCart ? (
                        <Button
                          id={`btn-incart-${question.id}`}
                          type="button"
                          variant="success"
                          size="sm"
                          disabled
                        >
                          <FaCheck className="me-1" /> Na lista
                        </Button>
                      ) : (
                        <Button
                          id={`btn-addcart-${question.id}`}
                          type="button"
                          variant="outline-primary"
                          size="sm"
                          aria-label="Adicionar questão à lista"
                          disabled={isAdding && addingId === question.id}
                          onClick={() => handleAdd(question.id)}
                        >
                          <FaPlus className="me-1" /> Adicionar
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-center gap-3 mt-2">
              <Button
                id="btn-banca-prev"
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <span className="text-muted small">
                Página {page} de {totalPages}
              </span>
              <Button
                id="btn-banca-next"
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}

      <QuestionCartButton />

      <ModalManage
        id={viewQuestionId}
        token={token}
        showModalManage={!!viewQuestionId}
        setShowModalManage={() => setViewQuestionId(null)}
      />
    </div>
  );
}
